/* ============================================================
   Storage — IndexedDB for records, localStorage for config.

   Hand-written rather than pulling in `idb`: the surface we need is
   small, and every kilobyte counts on the connections this product
   is designed for.

   Every operation is failure-tolerant. Safari private browsing, quota
   exhaustion and disabled storage are all normal conditions here, not
   exceptional ones — losing persistence must never lose the user's
   on-screen result (audit finding A-06).
   ============================================================ */

import { announceStorageRefused } from './storage-notice.js';

const DB_NAME = 'kanda';
// v2 adds the `barriers` store. The upgrade handler creates only stores
// that are missing, so an existing device gains it without losing data.
const DB_VERSION = 2;
const STORES = {
  scorecards: { keyPath: 'id' },
  benchmarks: { keyPath: 'id' },
  checklists: { keyPath: 'id' },
  barriers: { keyPath: 'id' }
};

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = request.result;
      for (const [name, config] of Object.entries(STORES)) {
        if (!db.objectStoreNames.contains(name)) {
          const store = db.createObjectStore(name, { keyPath: config.keyPath });
          store.createIndex('date', 'date', { unique: false });
        }
      }
      void event;
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('IndexedDB blocked by another tab'));
  }).catch((error) => {
    // Reset so a later call can retry rather than latching the failure.
    dbPromise = null;
    throw error;
  });

  return dbPromise;
}

function tx(storeName, mode, run) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, mode);
        const store = transaction.objectStore(storeName);
        let result;
        try {
          result = run(store);
        } catch (error) {
          reject(error);
          return;
        }
        transaction.oncomplete = () => resolve(result?.result ?? result);
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
      })
  );
}

export const idb = {
  async put(storeName, value) {
    return tx(storeName, 'readwrite', (store) => store.put(value));
  },

  async get(storeName, key) {
    return tx(storeName, 'readonly', (store) => store.get(key));
  },

  async getAll(storeName) {
    return tx(storeName, 'readonly', (store) => store.getAll());
  },

  /**
   * Just the number. The header badge was deserialising every saved
   * record on every route change to render a two-digit count — work that
   * grows with the user's history and is thrown away immediately.
   */
  async count(storeName) {
    return tx(storeName, 'readonly', (store) => store.count());
  },

  async delete(storeName, key) {
    return tx(storeName, 'readwrite', (store) => store.delete(key));
  },

  async clear(storeName) {
    return tx(storeName, 'readwrite', (store) => store.clear());
  },

  /**
   * Persist without ever propagating a failure to the caller.
   * Used where the record is a convenience and the render is the product.
   */
  async putQuiet(storeName, value) {
    try {
      await idb.put(storeName, value);
      return true;
    } catch (error) {
      console.warn(`Could not persist to "${storeName}":`, error);
      /* The no-throw contract is unchanged — the caller still gets
         `false` and the render is untouched. What changes is that the
         person looking at the screen finds out too. A console warning
         is a message to whoever has devtools open, which on the
         connections this product is built for is nobody. */
      announceStorageRefused({ error, where: storeName });
      return false;
    }
  },

  /**
   * Delete without throwing — but the caller MUST read the answer.
   *
   * This is not putQuiet's twin, and the difference is the point.
   * putQuiet exists because an automatic save is a convenience and the
   * render is the product: losing one silently costs the user nothing
   * they asked for. A delete is the opposite. The user pressed a button
   * and formed an expectation, so a delete that fails and says nothing
   * is worse than one that throws — the record is still there, the list
   * still shows it, and the only feedback is that pressing Delete does
   * nothing at all.
   *
   * `delete` propagated its rejection and two call sites awaited it
   * bare, which produced exactly that: in private mode, or with the
   * quota exhausted, or on a locked database, the click was inert and
   * unexplained.
   *
   * @returns {Promise<boolean>} true if the record is gone
   */
  async deleteQuiet(storeName, key) {
    try {
      await idb.delete(storeName, key);
      return true;
    } catch (error) {
      console.warn(`Could not delete from "${storeName}":`, error);
      announceStorageRefused({ error, where: storeName });
      return false;
    }
  }
};

/* ---- Session / config (localStorage, always guarded) ---- */

function safeStorage() {
  try {
    const probe = '__kanda_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

const store = safeStorage();
const memoryFallback = new Map();

export const session = {
  get(key, fallback = null) {
    try {
      const raw = store ? store.getItem(key) : memoryFallback.get(key);
      return raw === null || raw === undefined ? fallback : JSON.parse(raw);
    } catch {
      return fallback;
    }
  },

  set(key, value) {
    try {
      const raw = JSON.stringify(value);
      if (store) store.setItem(key, raw);
      else memoryFallback.set(key, raw);
      return true;
    } catch (error) {
      console.warn(`Could not store "${key}":`, error);
      announceStorageRefused({ error, where: key });
      return false;
    }
  },

  remove(key) {
    try {
      if (store) store.removeItem(key);
      else memoryFallback.delete(key);
    } catch {
      /* nothing useful to do */
    }
  }
};

/** Storage-independent id. crypto.randomUUID needs a secure context (A-25). */
export function newId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch {
      /* fall through */
    }
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
